import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Activity, RefreshCw, 
  Loader, Filter, ChevronLeft, ChevronRight, Zap, BarChart2,
  AlertCircle, Volume2, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import PageAwareAI from './PageAwareAI';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const MarketAnomalyDetector = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [showOnlyAnomalies, setShowOnlyAnomalies] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, category]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/anomalies/categories`);
      if (response.ok) {
        const result = await response.json();
        setCategories(result.categories || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      if (category) params.append('category', category);
      if (showOnlyAnomalies) params.append('anomalies', 'true');

      const response = await fetch(`${API_URL}/anomalies/scan?${params}`);
      if (!response.ok) throw new Error('Failed to fetch anomaly data');
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Anomaly fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setPage(1);
    fetchData();
  };

  const handleCategoryChange = (cat) => {
    setCategory(cat);
    setPage(1);
  };

  const getAnomalyColor = (type) => {
    switch (type) {
      case 'RSI_OVERBOUGHT':
      case 'LARGE_GAIN':
      case 'GAP_UP':
        return '#34d399';
      case 'RSI_OVERSOLD':
      case 'LARGE_LOSS':
      case 'GAP_DOWN':
        return '#f87171';
      case 'UNUSUAL_VOLUME':
        return '#fbbf24';
      case 'NEAR_52W_HIGH':
        return '#60a5fa';
      case 'NEAR_52W_LOW':
        return '#f97316';
      default:
        return '#94a3b8';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#60a5fa';
      default: return '#94a3b8';
    }
  };

  const getAnomalyIcon = (type) => {
    if (type.includes('RSI')) return <Activity size={14} />;
    if (type.includes('VOLUME')) return <Volume2 size={14} />;
    if (type.includes('GAP') || type.includes('LARGE')) return <BarChart2 size={14} />;
    if (type.includes('52W')) return <TrendingUp size={14} />;
    return <AlertCircle size={14} />;
  };

  const filterStocks = (stocks) => {
    if (!stocks) return [];
    let filtered = stocks;
    
    if (filterType !== 'all') {
      filtered = filtered.filter(stock => 
        stock.anomalies?.some(a => {
          switch (filterType) {
            case 'rsi': return a.type.includes('RSI');
            case 'volume': return a.type === 'UNUSUAL_VOLUME';
            case 'gaps': return a.type.includes('GAP');
            case 'moves': return a.type.includes('LARGE');
            default: return true;
          }
        })
      );
    }
    
    return filtered;
  };

  const filteredStocks = filterStocks(data?.stocks);

  if (error) {
    return (
      <div style={{ 
        background: 'rgba(239, 68, 68, 0.1)', 
        border: '1px solid rgba(239, 68, 68, 0.3)', 
        borderRadius: '0.75rem', 
        padding: '2rem',
        textAlign: 'center'
      }}>
        <AlertTriangle size={48} color="#f87171" style={{ margin: '0 auto 1rem' }} />
        <h3 style={{ color: '#f87171', marginBottom: '0.5rem' }}>Error Loading Data</h3>
        <p style={{ color: '#fca5a5' }}>{error}</p>
        <button onClick={handleRefresh} style={{
          marginTop: '1rem',
          padding: '0.5rem 1rem',
          background: '#ef4444',
          border: 'none',
          borderRadius: '0.5rem',
          color: 'white',
          cursor: 'pointer'
        }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)', 
        borderRadius: '0.75rem', 
        padding: '1.25rem', 
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <AlertTriangle size={24} />
              Market Anomaly Detector
            </h2>
            <p style={{ color: '#fecaca', fontSize: '0.75rem', margin: 0 }}>
              Live scanning of 100 stocks • RSI, Volume, Price Gaps
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600'
            }}
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#f87171' }}>{data.anomaliesOnPage || 0}</div>
            <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Anomalies</div>
          </div>
          <div style={{ background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#fbbf24' }}>{data.summary.unusualVolume || 0}</div>
            <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Volume</div>
          </div>
          <div style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#34d399' }}>{data.summary.rsiOverbought || 0}</div>
            <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Overbought</div>
          </div>
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#f87171' }}>{data.summary.rsiOversold || 0}</div>
            <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Oversold</div>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1rem',
        overflowX: 'auto',
        paddingBottom: '0.5rem',
        WebkitOverflowScrolling: 'touch'
      }}>
        <button
          onClick={() => handleCategoryChange('')}
          style={{
            padding: '0.5rem 0.75rem',
            background: category === '' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(51, 65, 85, 0.5)',
            border: category === '' ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid #475569',
            borderRadius: '0.5rem',
            color: category === '' ? '#f87171' : '#94a3b8',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          All (100)
        </button>
        {categories.map(cat => (
          <button
            key={cat.name}
            onClick={() => handleCategoryChange(cat.name)}
            style={{
              padding: '0.5rem 0.75rem',
              background: category === cat.name ? 'rgba(239, 68, 68, 0.2)' : 'rgba(51, 65, 85, 0.5)',
              border: category === cat.name ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid #475569',
              borderRadius: '0.5rem',
              color: category === cat.name ? '#f87171' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {cat.name} ({cat.count})
          </button>
        ))}
      </div>

      {/* Anomaly Type Filter */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1rem',
        flexWrap: 'wrap'
      }}>
        {[
          { key: 'all', label: 'All Types' },
          { key: 'rsi', label: 'RSI Extremes' },
          { key: 'volume', label: 'Unusual Volume' },
          { key: 'gaps', label: 'Price Gaps' },
          { key: 'moves', label: 'Large Moves' }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilterType(f.key)}
            style={{
              padding: '0.375rem 0.75rem',
              background: filterType === f.key ? 'rgba(96, 165, 250, 0.2)' : 'transparent',
              border: filterType === f.key ? '1px solid rgba(96, 165, 250, 0.5)' : '1px solid #475569',
              borderRadius: '0.375rem',
              color: filterType === f.key ? '#60a5fa' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.7rem',
              fontWeight: '500'
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem' }}>
          <Loader size={48} color="#f87171" className="spin" />
          <p style={{ color: '#94a3b8', marginTop: '1rem', fontSize: '0.875rem' }}>
            Scanning stocks for anomalies...
          </p>
          <p style={{ color: '#64748b', fontSize: '0.75rem' }}>
            This may take 15-20 seconds
          </p>
        </div>
      ) : (
        <>
          {/* Stock Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filteredStocks.length === 0 ? (
              <div style={{ 
                padding: '2rem', 
                textAlign: 'center', 
                background: 'rgba(51, 65, 85, 0.3)',
                borderRadius: '0.75rem'
              }}>
                <AlertCircle size={32} color="#64748b" style={{ margin: '0 auto 0.5rem' }} />
                <p style={{ color: '#94a3b8' }}>No anomalies found with current filters</p>
              </div>
            ) : (
              filteredStocks.map((stock, idx) => (
                <div
                  key={idx}
                  style={{
                    background: stock.hasAnomalies 
                      ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(30, 41, 59, 0.5) 100%)'
                      : 'rgba(30, 41, 59, 0.5)',
                    border: stock.hasAnomalies 
                      ? '1px solid rgba(239, 68, 68, 0.3)' 
                      : '1px solid #334155',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    transition: 'all 0.2s'
                  }}
                >
                  {/* Stock Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.125rem', fontWeight: 'bold' }}>{stock.symbol}</span>
                        <span style={{ 
                          fontSize: '0.6rem', 
                          padding: '0.125rem 0.375rem',
                          background: 'rgba(96, 165, 250, 0.2)',
                          borderRadius: '0.25rem',
                          color: '#60a5fa'
                        }}>
                          {stock.category}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                        Updated: {stock.lastUpdated}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.125rem', fontWeight: 'bold' }}>
                        ${stock.currentPrice?.toFixed(2)}
                      </div>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: stock.priceChange >= 0 ? '#34d399' : '#f87171',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '0.25rem'
                      }}>
                        {stock.priceChange >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                        {stock.priceChange >= 0 ? '+' : ''}{stock.priceChange?.toFixed(2)}%
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)', 
                    gap: '0.5rem',
                    marginBottom: '0.75rem',
                    fontSize: '0.7rem'
                  }}>
                    <div style={{ background: 'rgba(51, 65, 85, 0.5)', padding: '0.5rem', borderRadius: '0.375rem', textAlign: 'center' }}>
                      <div style={{ color: '#94a3b8' }}>RSI</div>
                      <div style={{ 
                        fontWeight: '600',
                        color: stock.rsi >= 70 ? '#34d399' : stock.rsi <= 30 ? '#f87171' : 'white'
                      }}>
                        {stock.rsi?.toFixed(1) || 'N/A'}
                      </div>
                    </div>
                    <div style={{ background: 'rgba(51, 65, 85, 0.5)', padding: '0.5rem', borderRadius: '0.375rem', textAlign: 'center' }}>
                      <div style={{ color: '#94a3b8' }}>Vol %</div>
                      <div style={{ 
                        fontWeight: '600',
                        color: stock.volumeChange >= 100 ? '#fbbf24' : 'white'
                      }}>
                        {stock.volumeChange >= 0 ? '+' : ''}{stock.volumeChange?.toFixed(0)}%
                      </div>
                    </div>
                    <div style={{ background: 'rgba(51, 65, 85, 0.5)', padding: '0.5rem', borderRadius: '0.375rem', textAlign: 'center' }}>
                      <div style={{ color: '#94a3b8' }}>Gap</div>
                      <div style={{ 
                        fontWeight: '600',
                        color: Math.abs(stock.priceGap) >= 2 
                          ? (stock.priceGap > 0 ? '#34d399' : '#f87171') 
                          : 'white'
                      }}>
                        {stock.priceGap >= 0 ? '+' : ''}{stock.priceGap?.toFixed(2)}%
                      </div>
                    </div>
                  </div>

                  {/* Anomaly Tags */}
                  {stock.anomalies && stock.anomalies.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                      {stock.anomalies.map((anomaly, aIdx) => (
                        <div
                          key={aIdx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            background: `${getAnomalyColor(anomaly.type)}20`,
                            border: `1px solid ${getAnomalyColor(anomaly.type)}40`,
                            borderRadius: '0.375rem',
                            fontSize: '0.65rem',
                            color: getAnomalyColor(anomaly.type)
                          }}
                        >
                          {getAnomalyIcon(anomaly.type)}
                          <span>{anomaly.description}</span>
                          <span style={{
                            padding: '0.0625rem 0.25rem',
                            background: getSeverityColor(anomaly.severity),
                            borderRadius: '0.25rem',
                            color: 'white',
                            fontSize: '0.55rem',
                            fontWeight: '700',
                            textTransform: 'uppercase'
                          }}>
                            {anomaly.severity}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No Anomalies */}
                  {(!stock.anomalies || stock.anomalies.length === 0) && (
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <Zap size={12} />
                      No anomalies detected - trading normally
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {data?.pagination && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: '1rem',
              marginTop: '1.5rem',
              padding: '1rem',
              background: 'rgba(30, 41, 59, 0.5)',
              borderRadius: '0.75rem'
            }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!data.pagination.hasPrevPage || loading}
                style={{
                  padding: '0.5rem 1rem',
                  background: data.pagination.hasPrevPage ? 'rgba(239, 68, 68, 0.2)' : 'rgba(51, 65, 85, 0.3)',
                  border: '1px solid',
                  borderColor: data.pagination.hasPrevPage ? 'rgba(239, 68, 68, 0.5)' : '#475569',
                  borderRadius: '0.5rem',
                  color: data.pagination.hasPrevPage ? '#f87171' : '#64748b',
                  cursor: data.pagination.hasPrevPage ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                <ChevronLeft size={18} />
                Prev
              </button>

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  Page {data.pagination.page} of {data.pagination.totalPages}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                  {data.pagination.totalStocks} total stocks
                </div>
              </div>

              <button
                onClick={() => setPage(p => p + 1)}
                disabled={!data.pagination.hasNextPage || loading}
                style={{
                  padding: '0.5rem 1rem',
                  background: data.pagination.hasNextPage ? 'rgba(239, 68, 68, 0.2)' : 'rgba(51, 65, 85, 0.3)',
                  border: '1px solid',
                  borderColor: data.pagination.hasNextPage ? 'rgba(239, 68, 68, 0.5)' : '#475569',
                  borderRadius: '0.5rem',
                  color: data.pagination.hasNextPage ? '#f87171' : '#64748b',
                  cursor: data.pagination.hasNextPage ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Info Box */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(251, 191, 36, 0.05) 100%)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        borderRadius: '0.75rem'
      }}>
        <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.5rem 0' }}>
          <AlertCircle size={16} color="#f87171" />
          What We Detect
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', fontSize: '0.75rem', color: '#cbd5e1' }}>
          <div><strong style={{ color: '#34d399' }}>RSI Overbought</strong>: RSI ≥ 70 (potential pullback)</div>
          <div><strong style={{ color: '#f87171' }}>RSI Oversold</strong>: RSI ≤ 30 (potential bounce)</div>
          <div><strong style={{ color: '#fbbf24' }}>Unusual Volume</strong>: 100%+ above 20-day avg</div>
          <div><strong style={{ color: '#60a5fa' }}>Price Gaps</strong>: 2%+ gap at market open</div>
          <div><strong style={{ color: '#a78bfa' }}>Large Moves</strong>: 5%+ price change in a day</div>
          <div><strong style={{ color: '#f97316' }}>52-Week Levels</strong>: Near high or low</div>
        </div>
      </div>
  <PageAwareAI 
  pageId="anomaly-detector"
  pageData={{
    anomalies: filteredStocks.filter(s => s.hasAnomalies),
    summary: data?.summary || {},
    totalScanned: data?.pagination?.totalStocks || 0,
    anomaliesFound: data?.anomaliesOnPage || 0,
    categories: categories.map(c => c.name),
    currentCategory: category || 'all',
    currentFilter: filterType
  }}
/>
    </div>
  );
};

export default MarketAnomalyDetector;